import React, { useState, useEffect } from 'react';
import { briefs, sources } from '../api';

const BriefScreen = ({ user }) => {
  const [brief, setBrief] = useState(null);
  const getContent = (b) => {
    if (!b) return null;
    const inner = typeof b.content === 'object' ? b.content : b;
    return inner;
  };
  const [sourceCount, setSourceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [briefRes, sourcesRes] = await Promise.all([
        briefs.today().catch(() => ({ data: null })),
        sources.list(),
      ]);
      setBrief(briefRes.data);
      setSourceCount(sourcesRes.data.length);
    } catch (err) {
      setError('Failed to load your brief.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await briefs.generate();
      console.log('BRIEF DATA:', JSON.stringify(res.data, null, 2));
      setBrief(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate brief.');
    } finally {
      setGenerating(false);
    }
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div className="skeleton" style={{ height: 80, borderRadius: 16, marginBottom: 24 }} />
          <div className="skeleton" style={{ height: 300, borderRadius: 16 }} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.grid} />

      <div style={styles.container}>
        {/* Date header */}
        <div style={styles.dateHeader}>
          <div style={styles.dateLabel}>TODAY'S BRIEF</div>
          <div style={styles.dateStr}>{dateStr}</div>
        </div>

        {/* Status bar */}
        <div style={styles.statusBar}>
          <div style={styles.statusItem}>
            <span style={styles.statusDot} />
            <span style={styles.statusText}>{sourceCount} sources monitored</span>
          </div>
          <div style={styles.statusDivider} />
          <div style={styles.statusItem}>
            <span style={{ ...styles.statusDot, background: 'var(--anima)', boxShadow: '0 0 6px var(--anima)' }} />
            <span style={styles.statusText}>ANIMA active</span>
          </div>
          {brief && (
            <>
              <div style={styles.statusDivider} />
              <div style={styles.statusItem}>
                <span style={styles.statusText}>
                  Generated {new Date(brief.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* No sources state */}
        {sourceCount === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>◎</div>
            <div style={styles.emptyTitle}>No sources configured</div>
            <div style={styles.emptySub}>
              Add RSS feeds in Sources and ANIMA will synthesize them into your daily brief.
            </div>
            <a href="/sources" className="btn btn-primary" style={{ textDecoration: 'none', marginTop: 8 }}>
              Add Sources
            </a>
          </div>
        )}

        {/* Brief content */}
        {brief && getContent(brief)?.anima_analysis && (
          <div style={styles.animaCard}>
            <div style={styles.animaHeader}>
              <div style={styles.animaBadge}>
                <span style={styles.animaPulse} />
                <span style={styles.animaLabel}>ANIMA ANALYSIS</span>
              </div>
              <span style={styles.animaHint}>Cross-source synthesis</span>
            </div>
            <div style={styles.animaBody}>
              {getContent(brief)?.anima_analysis}
            </div>
          </div>
        )}

        {brief && getContent(brief)?.sources && (
          <div style={styles.briefCard}>
            <div style={styles.briefHeader}>
              <span style={styles.briefLabel}>SUMMARY</span>
            </div>
            <div style={styles.briefContent}>
              {getContent(brief)?.sources?.map((src, i) => (
              <div key={i} style={styles.sourceBlock}>
                <div style={styles.sourceBlockName}>{src.source_name}</div>
                <p style={styles.briefPara}>{src.summary}</p>
              </div>
            ))}
            </div>
          </div>
        )}

        {/* No brief yet */}
        {!brief && sourceCount > 0 && (
          <div style={styles.noBriefCard}>
            <div style={styles.noBriefIcon}>◈</div>
            <div style={styles.noBriefTitle}>No brief for today yet</div>
            <div style={styles.noBriefSub}>
              ANIMA generates your brief automatically each morning,<br />
              or you can trigger one now.
            </div>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={generating}
              style={{ marginTop: 8 }}
            >
              {generating ? (
                <>
                  <span style={styles.spinner} />
                  ANIMA is thinking...
                </>
              ) : (
                '◈ Generate Now'
              )}
            </button>
          </div>
        )}

        {/* Regenerate button */}
        {brief && sourceCount > 0 && (
          <div style={styles.regenRow}>
            <button
              className="btn btn-ghost"
              onClick={handleGenerate}
              disabled={generating}
              style={{ fontSize: 13 }}
            >
              {generating ? 'Regenerating...' : '↻ Regenerate Brief'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
    position: 'relative',
  },
  grid: {
    position: 'fixed',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(0, 212, 255, 0.02) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 212, 255, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '64px 64px',
    pointerEvents: 'none',
    zIndex: 0,
  },
  container: {
    maxWidth: 760,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    position: 'relative',
    zIndex: 1,
  },
  dateHeader: {
    marginBottom: 4,
  },
  dateLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: 'var(--primary)',
    marginBottom: 6,
  },
  dateStr: {
    fontSize: 26,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '10px 16px',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--primary)',
    boxShadow: '0 0 6px var(--primary-glow)',
  },
  statusText: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    fontWeight: 500,
  },
  statusDivider: {
    width: 1,
    height: 14,
    background: 'var(--border)',
  },
  error: {
    background: 'rgba(255, 77, 109, 0.1)',
    border: '1px solid rgba(255, 77, 109, 0.3)',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--danger)',
  },
  animaCard: {
    background: 'var(--surface)',
    border: '1px solid rgba(123, 97, 255, 0.3)',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 0 40px rgba(123, 97, 255, 0.06)',
    animation: 'fadeIn 0.5s ease forwards',
  },
  animaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  animaBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'var(--anima-dim)',
    border: '1px solid rgba(123, 97, 255, 0.25)',
    borderRadius: 20,
    padding: '4px 12px',
  },
  animaPulse: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--anima)',
    boxShadow: '0 0 8px var(--anima)',
    display: 'inline-block',
    animation: 'pulse-glow 2s infinite',
  },
  animaLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'var(--anima)',
  },
  animaHint: {
    fontSize: 11,
    color: 'var(--text-secondary)',
  },
  animaBody: {
    fontSize: 15,
    lineHeight: 1.75,
    color: 'var(--text)',
    borderLeft: '2px solid var(--anima)',
    paddingLeft: 16,
  },
  briefCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 24,
    animation: 'fadeIn 0.5s ease 0.1s forwards',
    opacity: 0,
  },
  briefHeader: {
    marginBottom: 16,
  },
  briefLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: 'var(--text-secondary)',
  },
  briefContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  briefPara: {
    fontSize: 15,
    lineHeight: 1.75,
    color: 'var(--text)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  emptyIcon: {
    fontSize: 40,
    color: 'var(--text-dim)',
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--text)',
  },
  emptySub: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    maxWidth: 360,
    lineHeight: 1.6,
  },
  noBriefCard: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  noBriefIcon: {
    fontSize: 40,
    color: 'var(--primary)',
  },
  noBriefTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--text)',
  },
  noBriefSub: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
  },
  spinner: {
    width: 14,
    height: 14,
    border: '2px solid rgba(10,10,15,0.3)',
    borderTop: '2px solid var(--bg)',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.8s linear infinite',
  },
  sourceBlock: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: '1px solid var(--border)',
  },
  sourceBlockName: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: 'var(--primary)',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  regenRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 8,
  },
};

export default BriefScreen;
